import { create } from 'zustand';

interface EditedPriceManagerStore {
  pendingEditedPriceRequests: number;
  isPendingEditedPrice: boolean;
  pendingEditedPriceRows: Record<string, number>;
  startPendingEditedPrice: () => void;
  finishPendingEditedPrice: () => void;
  startPendingEditedPriceRow: (rowId: string) => void;
  finishPendingEditedPriceRow: (rowId: string) => void;
  isPendingEditedPriceRow: (rowId: string) => boolean;
}

export const useEditedPriceManagerStore = create<EditedPriceManagerStore>((set, get) => ({
  pendingEditedPriceRequests: 0,
  isPendingEditedPrice: false,
  pendingEditedPriceRows: {},
  startPendingEditedPrice: () =>
    set(state => {
      const pendingEditedPriceRequests = state.pendingEditedPriceRequests + 1;

      return {
        pendingEditedPriceRequests,
        isPendingEditedPrice: pendingEditedPriceRequests > 0,
      };
    }),
  finishPendingEditedPrice: () =>
    set(state => {
      const pendingEditedPriceRequests = Math.max(0, state.pendingEditedPriceRequests - 1);

      return {
        pendingEditedPriceRequests,
        isPendingEditedPrice: pendingEditedPriceRequests > 0,
      };
    }),
  startPendingEditedPriceRow: rowId =>
    set(state => ({
      pendingEditedPriceRows: {
        ...state.pendingEditedPriceRows,
        [rowId]: (state.pendingEditedPriceRows[rowId] ?? 0) + 1,
      },
    })),
  finishPendingEditedPriceRow: rowId =>
    set(state => {
      const pendingCount = state.pendingEditedPriceRows[rowId] ?? 0;
      const pendingEditedPriceRows = { ...state.pendingEditedPriceRows };

      if (pendingCount <= 1) {
        delete pendingEditedPriceRows[rowId];
      } else {
        pendingEditedPriceRows[rowId] = pendingCount - 1;
      }

      return { pendingEditedPriceRows };
    }),
  isPendingEditedPriceRow: rowId => Boolean(get().pendingEditedPriceRows[rowId]),
}));

export const withPendingEditedPrice = async <T>(operation: () => Promise<T>): Promise<T> => {
  useEditedPriceManagerStore.getState().startPendingEditedPrice();

  try {
    return await operation();
  } finally {
    useEditedPriceManagerStore.getState().finishPendingEditedPrice();
  }
};
